"use client";

import cx from "classnames";
import React from "react";
import { FileUploader } from "react-drag-drop-files";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { withQuery } from "with-query";

import AddressImportTable from "./AddressImportTable";
import InfoIcon from "./assets/info-icon.svg?react";
import TrashIcon from "./assets/trash.svg?react";
import UploadIcon from "./assets/upload.svg?react";
import FileIcon from "./assets/uploadedFile.svg?react";
import { factoryUseModalX } from "@/lib/factoryUseModalX";
import fetchSingleton from "@/lib/fetchSingleton";
import {
  extractErrorsFromPlainBackendAnswer,
  useFormValidation,
} from "@/lib/formValidationHelpers";
import { humanizeError } from "@/lib/humanizeError";
import { useAuth } from "@/lib/useAuth";
import { useDataList } from "@/lib/useData";
import { useProgramId } from "@/lib/useProgramId";
import { ImportInfo } from "@/ModalAddressImport/types";
import PartFormError from "@/PartFormError";
import PartFormSaveButton from "@/PartFormSaveButton";
import PartLoader from "@/PartLoader";
import PartModal, { type PartModalRef } from "@/PartModal";
import PartPendingActionWrapper from "@/PartPendingActionWrapper";
import formStyles from "@/styles/form.module.scss";
import { importMultipleAddressesSchemaJoi } from "@/validations";

import styles from "./ModalAddressImport.module.scss";

/** Polling interval in milliseconds for checking import status */
const POLLING_INTERVAL_MS = 4000;

interface IFormInput {
  program_id: number;
  file: File;
  quality_gate_threshold?: number;
}

export const useModalAddressImport = factoryUseModalX({
  queryName: "modalAddressImport",
});

/**
 * External validation allowance (docs/08-import-external-allowance.md).
 * Every cell comes display-ready from the backend; this only renders them.
 */
type ExternalAllowanceRow = {
  source: string;
  provider: string;
  used_today: string;
  limit: string;
  remaining: string;
  reset_in: string;
  is_low: boolean;
};

const ExternalAllowanceTable: React.FC = () => {
  const [rows] = useDataList<ExternalAllowanceRow>(
    process.env.NEXT_PUBLIC_API_URL + "/api/v1/import-tasks/external-allowance",
    { noCache: true },
  );

  // Collapsed by default; opens itself when an allowance is low so the
  // warning is never hidden.
  const [expanded, setExpanded] = React.useState(false);
  const isLow = !!rows?.some(row => row.is_low);
  React.useEffect(() => {
    if (isLow) setExpanded(true);
  }, [isLow]);

  if (!rows?.length) return null;

  return (
    <div className={styles.allowanceContainer}>
      <button
        type="button"
        className={cx(formStyles.formLabelBig, styles.allowanceToggle)}
        onClick={() => setExpanded(!expanded)}
        data-test-element-type="button"
        data-test-action-type="toggle-allowance"
      >
        <span className={styles.allowanceChevron}>{expanded ? "▾" : "▸"}</span>
        Validation Allowance
      </button>
      {expanded && (
      <table className={styles.allowanceTable}>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Requests today</th>
            <th>Daily quota</th>
            <th>Remaining</th>
            <th>Resets in</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.source}
              className={cx({ [styles.allowanceLow]: row.is_low })}
              data-test-element-type="allowance-row"
              data-test-provider={row.source.toLowerCase()}
            >
              <td>{row.provider}</td>
              <td>{row.used_today}</td>
              <td>{row.limit}</td>
              <td>{row.remaining}</td>
              <td>{row.reset_in}</td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
      {expanded && rows.some(row => row.is_low) && (
        <div className={styles.allowanceWarning}>
          Google validation allowance is nearly exhausted — validations may fail
          until it resets.
        </div>
      )}
    </div>
  );
};

export const AddressImport: React.FC = () => {
  const { finishInteraction, startEdit: viewTask } = useModalAddressImport();

  const { programId } = useProgramId();
  const { user } = useAuth();

  // Check if user is Super Admin
  const isSuperAdmin = user?.role?.code === "sa";

  const formMethods = useForm<IFormInput>({
    reValidateMode: "onSubmit",
    defaultValues: {
      program_id: programId,
      quality_gate_threshold: 80,
    },
  });

  const {
    reset,
    control,
    setError,
    clearErrors,
  } = formMethods;

  const { getValidatedValues, setBackendErrors, applyErrorsOnAllFields, hasErrors } =
    useFormValidation<IFormInput>({
      control,
      joiSchema: importMultipleAddressesSchemaJoi,
      setError,
      clearErrors,
    });

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const handleSubmit = React.useCallback(
    (submitFunction: () => Promise<void>) =>
      async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        setIsSubmitting(true);
        try {
          await submitFunction();
        } catch (e) {
          console.error(e);
          toast.error(humanizeError(e));
        }
        setIsSubmitting(false);
      },
    [getValidatedValues, hasErrors, applyErrorsOnAllFields, setBackendErrors],
  );

  const { idToken } = useAuth();

  const onSubmit = async () => {
    if (hasErrors && !process.env.NEXT_PUBLIC_SHOULD_VALIDATE_ONLY_ON_BACKEND) {
      await new Promise(r => setTimeout(r, 1000));
      applyErrorsOnAllFields();
      return;
    }

    const data = getValidatedValues();
    const INTERRUPT = Symbol("INTERRUPT");
    try {
      const [uploadUrl, uploadKey] = await (async () => {
        const result = await fetchSingleton(
          withQuery(
            process.env.NEXT_PUBLIC_API_URL + `/api/v1/address/generate-upload-url`,
            {
              filename: data.file?.name,
            },
          ),
          { headers: { Authorization: `Bearer ${idToken}` } },
        );

        const { isValid, fieldErrors, globalErrors } =
          extractErrorsFromPlainBackendAnswer(result);
        setBackendErrors(fieldErrors);
        applyErrorsOnAllFields();
        globalErrors.forEach(it => toast.error(it.message));
        if (!isValid) throw INTERRUPT;

        const { uploadUrl, key } = result.list[0];

        return [uploadUrl as string, key as string];
      })();

      await (async () => {
        if (!uploadUrl)
          throw new Error(
            "Upload URL not returned by the server. Check backend response or configuration.",
          );
        await fetch(uploadUrl, {
          method: "PUT",
          body: data.file,
          headers: {
            // ["Content-Type"]: `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`,
            ["Content-Type"]: data.file?.type,
          },
        });
      })();

      const importTaskId = await (async () => {
        if (!uploadKey)
          throw new Error(
            "Upload KEY not returned by the server. Check backend response or configuration.",
          );

        // Only Super Admins can override quality gate threshold
        const queryParams: Record<string, unknown> = {
          s3_key: uploadKey,
          program_id: programId,
        };
        if (isSuperAdmin) {
          queryParams.quality_gate_threshold = data.quality_gate_threshold;
        }

        const url = withQuery(
          process.env.NEXT_PUBLIC_API_URL + `/api/v1/address/import`,
          queryParams,
        );

        const result = await fetchSingleton(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
          noSideEffects: true, // Don't trigger global data refresh - import doesn't affect other data
        });

        const { isValid, fieldErrors, globalErrors } = extractErrorsFromPlainBackendAnswer(result);
        setBackendErrors(fieldErrors);
        applyErrorsOnAllFields();
        globalErrors.forEach(it => toast.error(it.message));
        if (!isValid) throw INTERRUPT;

        return result.list[0].importTaskId;
      })();

      console.log("[Import] Received task_id:", importTaskId);

      finishInteraction();
      reset();


      // TODO Change Router push to router replace
      viewTask(importTaskId);

      // TODO: start POLLINg!!
      // add here

      const msg = "File successfully uploaded";
      toast.success(msg, { autoClose: 5000 });
    } catch (e) {
      if (e !== INTERRUPT) toast.error(humanizeError(e, "Create import"));
    }
  };



  return (
    <FormProvider {...formMethods}>
      <PartLoader block>
        <form
          className={formStyles.form}
          onSubmit={handleSubmit(onSubmit)}
          onReset={() => {
            finishInteraction();
            reset();
          }}
          autoComplete="off"
        >
          <label className={formStyles.formLabelBig} htmlFor="file">
            Upload File <sup>*</sup>
          </label>
          {/* Docs: https://www.npmjs.com/package/react-drag-drop-files */}
          <Controller
            control={control}
            name="file"
            render={({ field, fieldState }) => {
              const file = field.value;

              const handleRemove = (e: React.MouseEvent) => {
                e.stopPropagation();
                field.onChange(undefined); // Clear file
              };

              const handleChange = (file: File | File[]) => {
                field.onChange(Array.isArray(file) ? file[0] : file);
              };

              return (
                <FileUploader
                  classes={cx("uploadZone", formStyles.formUploadZone, {
                    [formStyles.invalid]: !!fieldState.error,
                  })}
                  handleChange={handleChange}
                  types={["XLS", "XLSX", "XLSB"]}
                  disabled={!!file}
                >
                  {!file ? (
                    <div className={styles.uploadFieldContainerWithoutFile} id="file">
                      <UploadIcon />
                      <span>
                        Drag & drop file here or <i>Choose file</i> to upload
                      </span>
                    </div>
                  ) : (
                    <div className={styles.uploadFieldContainerWithFile}>
                      <FileIcon />
                      <div className={styles.fileInfo}>
                        <div className={styles.fileInfoName}>{file.name}</div>
                        <div className={styles.fileInfoSize}>
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemove}
                        className={styles.removeFileButton}
                        data-test-element-type="button"
                        data-test-action-type="remove"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  )}
                </FileUploader>
              );
            }}
          />
          <PartFormError name={["file", "query.filename"]} />

          {/* Quality Gate Threshold dropdown - visible only for Super Admins */}
          {isSuperAdmin && (
            <>
              <label className={formStyles.formLabelBig} htmlFor="quality_gate_threshold">
                Quality Gate Threshold (%)
              </label>
              <Controller
                control={control}
                name="quality_gate_threshold"
                render={({ field }) => (
                  <select
                    id="quality_gate_threshold"
                    className={formStyles.formInput}
                    value={field.value}
                    onChange={e => field.onChange(Number(e.target.value))}
                  >
                    {Array.from({ length: 101 }, (_, i) => (
                      <option key={i} value={i}>
                        {i}%
                      </option>
                    ))}
                  </select>
                )}
              />
            </>
          )}

          <ExternalAllowanceTable />

          <div className={formStyles.formActionButtonsContainer}>
            <button
              type="reset"
              className={formStyles.buttonReset}
              data-test-element-type="button"
              data-test-action-type="reset"
            >
              Discard
            </button>
            <PartFormSaveButton isSubmitting={isSubmitting}>Import</PartFormSaveButton>
          </div>
        </form>
      </PartLoader>
    </FormProvider>
  );
};

const formatTimeSpent = (startedAt?: string, updatedAt?: string): string => {
  if (!startedAt || !updatedAt) return "-";
  const start = new Date(startedAt).getTime();
  const end = new Date(updatedAt).getTime();
  const diffMs = end - start;
  if (diffMs < 0) return "-";

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

const CustomTitleWithProgress: React.FC<{
  importInfo?: ImportInfo;
}> = ({ importInfo }) => {
  const status = importInfo?.status;
  const statusJson = importInfo?.status_json;

  // Derive state from status
  const isRunning = !!status && !["COMPLETED", "FAILED"].includes(status);
  const isFailed = status === "FAILED";
  const isCompleted = status === "COMPLETED";
  const isLoaded = !!importInfo;

  const statusIcon = isRunning ? "⏳" : isCompleted ? "✅" : isFailed ? "❌" : null;

  // External validation progress for the single progress bar
  const extValidationCurrent = statusJson?.addresses_external_validation_current || 0;
  const extValidationTotal = statusJson?.addresses_external_validation_total || 0;
  const extValidationPercent = extValidationTotal > 0 ? Math.round((extValidationCurrent / extValidationTotal) * 100) : 0;

  // Time spent calculation
  const timeSpent = formatTimeSpent(importInfo?.started_at, importInfo?.updated_at);

  return (
    <PartLoader active={!isLoaded} block transitionDuration={1500}>
      <div className={styles.titleContainer}>
        <div className={styles.titleLeftColumn}>
          <div className={styles.headerLabel}><span style={{fontSize: '0.9em', verticalAlign: 'middle', lineHeight: 0}}>{statusIcon}</span> Import status: <span className={cx(styles.headerLabel, isCompleted && styles.statusGreen, isFailed && styles.statusRed)}>{importInfo?.status}</span></div>
          <div className={cx(styles.titleFilename, styles.regularText)} style={{marginTop: '4px'}}>{importInfo?.filename || "..."}</div>
        </div>
        <div className={styles.titleStatsContainer}>
          <div>
            <div className={styles.headerLabel}>Total<InfoIcon className={styles.infoIcon} data-tooltip-id="stat-total" /></div> 
            <Tooltip id="stat-total" place="bottom" className={styles.tooltip}>Total number of address rows found in the uploaded Excel file including duplicates.</Tooltip>
            <span className={styles.regularText}>{statusJson?.total_rows ?? "-"}</span>
          </div>
          <div>
            <div className={styles.headerLabel}>VanMap<InfoIcon className={styles.infoIcon} data-tooltip-id="stat-vanmap" /></div>
            <Tooltip id="stat-vanmap" place="bottom" className={styles.tooltip}>Addresses pulled from VanMap database as primaries and/or alternates for matching primary and alternate addresses.</Tooltip>
            <span className={styles.regularText}>{statusJson?.addresses_new_vanmap ?? "-"}</span>
          </div>
          <div>
            <div className={styles.headerLabel}>Verified<InfoIcon className={styles.infoIcon} data-tooltip-id="stat-verified" /></div> 
            <Tooltip id="stat-verified" place="bottom" className={styles.tooltip}>Addresses successfully validated through VanMap, NAR, or external AWS and Google services.</Tooltip>
            <span className={styles.regularText}>{statusJson?.addresses_valid ?? "-"}</span>
          </div>
          <div>
            <div className={styles.headerLabel}>Unverified<InfoIcon className={styles.infoIcon} data-tooltip-id="stat-unverified" /></div> 
            <Tooltip id="stat-unverified" place="bottom" className={styles.tooltip}>Addresses that failed validation in all sources and could not be verified by any geocoding service.</Tooltip>
            <span className={styles.regularText}>{statusJson?.addresses_invalid ?? "-"}</span>
          </div>
          <div>
            <div className={styles.headerLabel}>Skipped<InfoIcon className={styles.infoIcon} data-tooltip-id="stat-skipped" /></div> 
            <Tooltip id="stat-skipped" place="bottom" className={styles.tooltip}>Addresses skipped due to being duplicates within the Excel file or already existing in the HHDB database<br/> Duplicates in excel: {statusJson?.addresses_duplicates_excel ?? "-"}<br/>Existing in HHDB: {statusJson?.addresses_duplicates_hhdb ?? "-"  }</Tooltip>
            <span className={styles.regularText}>{statusJson?.addresses_skipped ?? "-"}</span>
          </div>
        </div>
        <div className={styles.titleProgressContainer}>
          <MultiProgress
            items={
              [
                {
                  label: "External Validation",
                  valuePercent: extValidationPercent,
                  valueString: <>{extValidationCurrent} of {extValidationTotal}</>,
                  color: isFailed ? "#ef4444" : isCompleted ? "#00714bff" : "gray",
                },
              ] as ProgressItem[]
            }
            animate={isRunning}
          />
        </div>
      </div>
      <div className={styles.statusFooter}>
        {importInfo?.status_description && (
          <div className={cx(styles.regularText, styles.statusDescription, isCompleted && styles.statusGreen, isFailed && styles.statusRed)} title={importInfo.status_description}>{importInfo.status_description}</div>
        )}
        <span className={cx(styles.regularText, styles.timeSpent)} style={{flexWrap: 'wrap'}}>Time spent: {timeSpent}</span>
      </div>
    </PartLoader>
  );
};

const ModalAddressImport = React.forwardRef<PartModalRef>((_props, ref) => {
  const { isCreating, editingId: taskId, finishInteraction } = useModalAddressImport();
  const { idToken } = useAuth();

  // Native AJAX polling for import task status
  const [importInfo, setImportInfo] = React.useState<ImportInfo | undefined>(undefined);
  const status = importInfo?.status;
  const isRunning = !!taskId && status !== "COMPLETED" && status !== "FAILED";

  React.useEffect(() => {
    setImportInfo(undefined); // Reset on taskId change

    if (!taskId || !idToken) return;

    let cancelled = false;

    const fetchStatus = async () => {
      const url = `${process.env.NEXT_PUBLIC_API_URL}/api/v1/import-tasks/${taskId}?_no_cache=${Date.now()}`;
      const response = await fetch(url, {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });
      if (cancelled) return;

      const json = await response.json();
      const data = json?.list?.[0] as ImportInfo | undefined;

      // If task not found, mark as failed to stop polling
      if (!data) {
        setImportInfo({ status: "FAILED" } as ImportInfo);
        return;
      }

      setImportInfo(data);

      const currentStatus = data.status;
      const stillRunning = currentStatus !== "COMPLETED" && currentStatus !== "FAILED";
      if (stillRunning && !cancelled) {
        setTimeout(fetchStatus, POLLING_INTERVAL_MS);
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [taskId, idToken]);

  const downloadResults = React.useCallback(async () => {
    const url = withQuery(
      process.env.NEXT_PUBLIC_API_URL + `/api/v1/import-tasks/${taskId}/result`,
      {},
    );
    const response = await fetchSingleton(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (response?.status === "ERROR") {
      toast.error(humanizeError(response));
      return;
    }
    if (!response.downloadUrl) {
      toast.error(humanizeError("No download link received. Please try again or contact support."));
      return;
    }
    const link = document.createElement("a");
    link.href = response.downloadUrl;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [idToken, taskId]);

  return (
    <PartModal
      ref={ref}
      showed={isCreating || !!taskId}
      title={taskId ? <CustomTitleWithProgress importInfo={importInfo} /> : "Import Multiple Addresses"}
      onClose={() => finishInteraction()}
      fullCover={!!taskId}
      titleWidth100
      confirmCloseQuestion={isRunning ? "Import is in progress. Are you sure you want to close it?" : false}
    >
      <div className={styles.modalContent}>
        {!taskId && <AddressImport />}
        {taskId && !isRunning && (
          <AddressImportTable
            className={styles.addressImportTable}
            importTaskId={String(taskId)}
            importIsRunning={isRunning}
          />
        )}
        {taskId && (
          <div className={styles.modalButtonsContainer}>
            {importInfo?.s3_key_result && (
              <div className={styles.modalButtonWrapper}>
                <PartPendingActionWrapper
                  onClick={async () => {
                    if (isRunning) return;
                    await downloadResults();
                  }}
                  pending={isRunning}
                  block
                >
                  <button
                    type="button"
                    className={cx(formStyles.buttonSubmit, { [formStyles.disabled]: isRunning })}
                    data-test-element-type="button"
                    data-test-action="download-file"
                    title={isRunning ? "Import still in progress" : undefined}
                  >
                    Download results
                  </button>
                </PartPendingActionWrapper>
              </div>
            )}
            <div className={styles.modalButtonWrapperSecond}>
              <PartLoader active={isRunning} block>
                <button
                  type="button"
                  className={formStyles.buttonSubmit}
                  data-test-element-type="button"
                  data-test-action="close-import-modal"
                  onClick={() => finishInteraction()}
                >
                  Close
                </button>
              </PartLoader>
            </div>
          </div>
        )}
      </div>
    </PartModal>
  );
});

ModalAddressImport.displayName = "ModalAddressImport";

export default ModalAddressImport;

type ProgressItem = {
  label: string;
  valuePercent: number; // от 0 до 100
  valueString: string | React.ReactElement;
  color?: string;
};

type MultiProgressProps = {
  items: ProgressItem[];
  animate?: boolean;
};

const MultiProgress: React.FC<MultiProgressProps> = ({ items, animate }) => {
  return (
    <div className={styles.multiProgressContainer}>
      {items.map((item, index) => (
        <div key={item.label + index} className={styles.multiProgressItem}>
          <div className={styles.multiProgressHeader}>
            <span className={styles.headerLabel}>{item.label}</span>
            <span className={cx(styles.headerLabel, styles.multiProgressValue)}>{item.valueString}</span>
          </div>
          <div className={styles.multiProgressTrack}>
            <div
              style={{
                height: "100%",
                width: `${item.valuePercent}%`,
                backgroundColor: item.color || "#007bff",
                transition: "width 0.4s ease",
              }}
            />
            {animate && <div className={styles.multiProgressStripeOverlay} />}
          </div>
        </div>
      ))}
    </div>
  );
};
